<script>
    import { onMount } from "svelte";
    import { fly } from "svelte/transition";

    import { Router, Route } from "svelte-routing";

    import Menu from "./Menu/Menu.svelte";
    import RecentlyAdded from "./RecentlyAdded/RecentlyAdded.svelte";
    import Albums from "./Albums/Albums.svelte";
    import Playlists from "./Playlists/Playlists.svelte";
    import Export from "./Export/Export.svelte";
    import Import from "./Import/Import.svelte";

    import ContextMenu from "./components/ContextMenu.svelte";
    import CollectionModal from "./components/CollectionModal.svelte";

    import Toasts from "./components/toasts/Toasts.svelte";

    const baseURL = "";

    const music = MusicKit.getInstance();

    let deployed = false;

    let maxWidthQuery = window.matchMedia("(max-width: 900px)");
    let colorSchemeQuery = window.matchMedia("(prefers-color-scheme: dark)");

    onMount(() => {
        const invalidateMaxWidth = () => {
            maxWidthQuery = maxWidthQuery;
        };
        const invalidateColorScheme = () => {
            colorSchemeQuery = colorSchemeQuery;
        };
        maxWidthQuery.addListener(invalidateMaxWidth);
        colorSchemeQuery.addListener(invalidateColorScheme);
        return function () {
            maxWidthQuery.removeListener(invalidateMaxWidth);
            colorSchemeQuery.removeListener(invalidateColorScheme);
        };
    });

    $: mobile = maxWidthQuery.matches;
    $: themeColor = colorSchemeQuery.matches ? "#272727" : "#E0E0E0";

    let contextItem = undefined;
    let contextTop = 0;
    let contextLeft = 0;
    let contextTarget;
    function handleContextMenu(event) {
        const { item, pageX, pageY, target } = event.detail;
        contextItem = item;
        contextTop = pageY;
        contextLeft = pageX;
        contextTarget = target;
    }

    let modalItem = undefined;
    function handleModal(event) {
        modalItem = event.detail;
        // console.log("modalItem:", modalItem);
    }
</script>

<Router url={baseURL}>
    <div class="app {deployed ? 'opened' : 'closed'}">
        <Toasts position={mobile ? "bottom" : "top-right"}>
            {#if mobile}
                <div class="navbar">
                    MusicKit Player
                    <div
                        class="icon-menu burger"
                        on:click={() => (deployed = true)}
                    />
                </div>
            {/if}
            <div class="menu">
                <Menu
                    closable={maxWidthQuery.matches}
                    on:close={() => (deployed = false)}
                    on:open-modal={handleModal}
                    on:context-menu={handleContextMenu}
                />
            </div>
            <div class="content">
                <Route path="for-you" let:location>
                    <div
                        class="transition"
                        in:fly={{ x: -20, duration: 500 }}
                        out:fly={{ x: 20, duration: 500 }}
                    >
                        <div class="message">
                            This section is a Work In Progress.
                        </div>
                    </div>
                </Route>
                <Route path="recently-added" let:location>
                    <div
                        class="transition"
                        in:fly={{ x: -20, duration: 500 }}
                        out:fly={{ x: 20, duration: 500 }}
                    >
                        <RecentlyAdded
                            on:open-modal={handleModal}
                            on:context-menu={handleContextMenu}
                        />
                    </div>
                </Route>
                <Route path="albums" let:location>
                    <div
                        class="transition"
                        in:fly={{ x: -20, duration: 500 }}
                        out:fly={{ x: 20, duration: 500 }}
                    >
                        <Albums
                            on:open-modal={handleModal}
                            on:context-menu={handleContextMenu}
                        />
                    </div>
                </Route>
                <Route path="playlists" let:location>
                    <div
                        class="transition"
                        in:fly={{ x: -20, duration: 250 }}
                        out:fly={{ x: 20, duration: 250 }}
                    >
                        <Playlists
                            on:open-modal={handleModal}
                            on:context-menu={handleContextMenu}
                        />
                    </div>
                </Route>
                <Route path="artists" let:location>
                    <div
                        class="transition"
                        in:fly={{ x: -20, duration: 500 }}
                        out:fly={{ x: 20, duration: 500 }}
                    >
                        <div class="message">
                            This section is a Work In Progress.
                        </div>
                    </div>
                </Route>
                <Route path="songs" let:location>
                    <div
                        class="transition"
                        in:fly={{ x: -20, duration: 500 }}
                        out:fly={{ x: 20, duration: 500 }}
                    >
                        <div class="message">
                            This section is a Work In Progress.
                        </div>
                    </div>
                </Route>
                <Route path="export" let:location>
                    <div
                        class="transition"
                        in:fly={{ x: -20, duration: 500 }}
                        out:fly={{ x: 20, duration: 500 }}
                    >
                        <Export
                            on:open-modal={handleModal}
                            on:context-menu={handleContextMenu}
                        />
                    </div>
                </Route>
                <Route path="import" let:location>
                    <div
                        class="transition"
                        in:fly={{ x: -20, duration: 500 }}
                        out:fly={{ x: 20, duration: 500 }}
                    >
                        <Import
                            on:open-modal={handleModal}
                            on:context-menu={handleContextMenu}
                        />
                    </div>
                </Route>
                <Route>
                    <script>
                        navigate("/recently-added", { replace: true });
                    </script>
                </Route>
                {#if modalItem}
                    {#key modalItem.id}
                        <CollectionModal
                            id={modalItem.id}
                            type={modalItem.type}
                            on:open-modal={handleModal}
                            on:context-menu={handleContextMenu}
                            on:dismiss={() => (modalItem = undefined)}
                        />
                    {/key}
                {/if}
            </div>
            {#if contextItem}
                <ContextMenu
                    top={contextTop}
                    left={contextLeft}
                    item={contextItem}
                    target={contextTarget}
                    on:open-modal={handleModal}
                    on:dismiss={() => (contextItem = undefined)}
                />
            {/if}
        </Toasts>
    </div>
</Router>

<svelte:head>
    <meta name="theme-color" content={themeColor} />
</svelte:head>

<style>
    .content {
        position: relative;
    }

    .transition {
        width: 100%;
        height: 100%;
        position: absolute;
    }

    .app {
        position: relative;
        display: grid;
        grid-template-columns: 300px 1fr;
        grid-template-rows: 1fr;
        grid-template-areas: "menu content";
        width: 100%;
        height: 100%;
        background-color: var(--app-bg-color);
        color: var(--app-fg-color);
        overflow: hidden;

        --app-title-text-shadow: none;
        --app-title-font-size: 27px;
        --app-title-font-weight: bold;
        --app-title-height: 65px;

        --app-light-fg-color: #171717;
        --app-light-bg-color: #e0e0e0;
        --app-light-secondary-fg-color: #5b5b5b;
        --app-light-secondary-bg-color: #cfcfcf;
        --app-light-header-fg-color: #171717;
        --app-light-header-bg-color: #aaa;
        --app-light-explicit-fg-color: #171717;
        --app-light-explicit-bg-color: #999;
        --app-light-placeholder-color: #777;

        --app-dark-fg-color: #e0e0e0;
        --app-dark-bg-color: #272727;
        --app-dark-secondary-fg-color: #ccc;
        --app-dark-secondary-bg-color: #212121;
        --app-dark-header-fg-color: #e0e0e0;
        --app-dark-header-bg-color: #171717;
        --app-dark-explicit-fg-color: #171717;
        --app-dark-explicit-bg-color: #999;
        --app-dark-placeholder-color: #AAA;

        --app-fg-color: var(--app-light-fg-color);
        --app-bg-color: var(--app-light-bg-color);
        --app-secondary-fg-color: var(--app-light-secondary-fg-color);
        --app-secondary-bg-color: var(--app-light-secondary-bg-color);
        --app-header-fg-color: var(--app-light-header-fg-color);
        --app-header-bg-color: var(--app-light-header-bg-color);
        --app-explicit-fg-color: var(--app-light-explicit-fg-color);
        --app-explicit-bg-color: var(--app-light-explicit-bg-color);
        --app-placeholder-color: var(--app-light-placeholder-color);

        --app-inverse-fg-color: var(--app-dark-fg-color);
        --app-inverse-bg-color: var(--app-dark-bg-color);
        --app-inverse-secondary-fg-color: var(--app-dark-secondary-fg-color);
        --app-inverse-secondary-bg-color: var(--app-dark-secondary-bg-color);
        --app-inverse-header-fg-color: var(--app-dark-header-fg-color);
        --app-inverse-header-bg-color: var(--app-dark-header-bg-color);
        --app-inverse-placeholder-color: var(--app-dark-placeholder-color);

        /* --app-fg-color: #171717;
        --app-bg-color: #e0e0e0;
        --app-secondary-fg-color: #707070;
        --app-secondary-bg-color: #cfcfcf;
        --app-header-fg-color: #171717;
        --app-header-bg-color: #aaa;
        --app-explicit-fg-color: #171717;
        --app-explicit-bg-color: #999;

        --app-inverse-fg-color: #e0e0e0;
        --app-inverse-bg-color: #272727;
        --app-inverse-secondary-fg-color: #888;
        --app-inverse-secondary-bg-color: #212121;
        --app-inverse-header-fg-color: #e0e0e0;
        --app-inverse-header-bg-color: #171717; */

        --app-box-shadow: 0 0 10px -2px #000;

        --menu-title-text-shadow: var(--app-title-text-shadow);
        --menu-title-font-size: var(--app-title-font-size);
        --menu-title-font-weight: var(--app-title-font-weight);
        --menu-title-height: var(--app-title-height);
        --menu-fg-color: var(--app-fg-color);
        --menu-bg-color: var(--app-bg-color);
        --menu-opt-header-fg-color: var(--app-header-fg-color);
        --menu-opt-header-bg-color: var(--app-header-bg-color);
        --menu-opt-fg-color: var(--app-fg-color);
        --menu-opt-bg-color: var(--app-secondary-bg-color);
        --menu-search-fg-color: var(--app-fg-color);
        --menu-search-bg-color: var(--app-secondary-bg-color);
        --menu-search-results-fg-color: var(--app-inverse-fg-color);
        --menu-search-results-bg-color: var(--app-inverse-bg-color);
        --menu-search-results-header-fg-color: var(
            --app-inverse-header-fg-color
        );
        --menu-search-results-header-bg-color: var(
            --app-inverse-header-bg-color
        );
        --menu-auth-border: 2px solid var(--app-fg-color);
        --menu-auth-fg-color: var(--app-fg-color);
        --menu-auth-bg-color: var(--app-bg-color);
        --menu-auth-hover-fg-color: var(--app-bg-color);
        --menu-auth-hover-bg-color: var(--app-fg-color);

        --player-fg-color: var(--app-fg-color);
        --player-bg-color: var(--app-secondary-bg-color);
    }

    .menu {
        grid-area: menu;
        overflow-x: hidden;
        overflow-y: auto;
        scroll-behavior: smooth;
        -webkit-overflow-scrolling: touch;
        will-change: transform;
        transition: transform 0.2s, opacity 0.2s;
    }

    .content {
        grid-area: content;
        overflow: hidden;
    }

    .message {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        font-size: 20px;
    }

    @media screen and (max-width: 900px) {
        .app {
            grid-template-rows: 40px 1fr;
            grid-template-columns: 1fr;
            grid-template-areas: "navbar" "content";

            --app-title-font-size: 20px;
            --app-title-font-weight: bold;
            --app-title-height: 40px;
        }

        .menu {
            position: absolute;
            top: 0;
            right: 0;
            width: 300px;
            height: 100%;
            z-index: 999;
        }

        .app.closed .menu {
            transform: translateX(100%);
            opacity: 0;
        }

        .app.opened .menu {
            transform: none;
            opacity: 1;
        }

        .navbar {
            grid-area: navbar;
            width: 100%;
            height: 100%;
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            color: var(--app-fg-color);
            background-color: var(--app-bg-color);
            font-size: var(--app-title-font-size);
            font-weight: var(--app-title-font-weight);
            text-shadow: var(--app-title-text-shadow);
        }

        .burger {
            position: absolute;
            right: 0;
            top: 0;
            width: 60px;
            height: var(--app-title-height);
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
        }
    }

    @media (prefers-color-scheme: dark) {
        .app {
            --app-fg-color: var(--app-dark-fg-color);
            --app-bg-color: var(--app-dark-bg-color);
            --app-secondary-fg-color: var(--app-dark-secondary-fg-color);
            --app-secondary-bg-color: var(--app-dark-secondary-bg-color);
            --app-header-fg-color: var(--app-dark-header-fg-color);
            --app-header-bg-color: var(--app-dark-header-bg-color);
            --app-explicit-fg-color: var(--app-dark-explicit-fg-color);
            --app-explicit-bg-color: var(--app-dark-explicit-bg-color);
            --app-placeholder-color: var(--app-dark-placeholder-color);

            --app-inverse-fg-color: var(--app-light-fg-color);
            --app-inverse-bg-color: var(--app-light-bg-color);
            --app-inverse-secondary-fg-color: var(
                --app-light-secondary-fg-color
            );
            --app-inverse-secondary-bg-color: var(
                --app-light-secondary-bg-color
            );
            --app-inverse-header-fg-color: var(--app-light-header-fg-color);
            --app-inverse-header-bg-color: var(--app-light-header-bg-color);
            --app-inverse-placeholder-color: var(--app-light-placeholder-color);

            --app-title-text-shadow: 0 0 5px #227fff, 0 0 10px #227fff;
            --app-box-shadow: 0 0 10px 5px #000;
        }
    }
</style>
